import { PlayArrow, RestartAlt, Stop } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { Pm2IndexRequest, Pm2IndexResponse } from 'RtModels';
import { Pm2Router } from 'RtUi/app/rtAdmin/Pm2/Pm2.router';
import {
	pm2RestartProcess,
	pm2StartProcess,
	pm2StopProcess,
	useGetPm2
} from 'RtUi/app/rtAdmin/Pm2/service';
import DataGrid from 'RtUi/components/data/DataGrid';
import { getPm2Columns } from 'RtUi/components/data/DataGrid/configurations/rtAdm/pm2';

interface IPm2GridProps {
	onRowClick?: (row: Pm2IndexResponse) => void;
	resourceParams?: Pm2IndexRequest;
}

export const Pm2Grid = ({ resourceParams }: IPm2GridProps) => {
	const { data, isLoading, refetch } = useGetPm2(resourceParams);

	const stopProcess = async (row: Pm2IndexResponse) => {
		await pm2StopProcess(row);
		refetch();
	};

	const startProcess = async (row: Pm2IndexResponse) => {
		await pm2StartProcess(row);
		refetch();
	};

	const restartProcess = async (row: Pm2IndexResponse) => {
		await pm2RestartProcess(row);
		refetch();
	};

	const columns = useMemo(() => getPm2Columns(), []);

	const inlineActions = (row: Pm2IndexResponse) => (
		<Box sx={{ display: 'flex', justifyContent: 'left' }}>
			{Boolean(row.status === 'online') && (
				<Tooltip title="Stop Process" arrow placement="right">
					<IconButton
						type="button"
						color="error"
						size="small"
						disabled={row.status != 'online'}
						onClick={(e) => {
							stopProcess(row);
							e.stopPropagation();
						}}
						sx={{
							padding: 0
						}}
					>
						<Stop />
					</IconButton>
				</Tooltip>
			)}
			{Boolean(
				row.status === 'online' &&
					!(row.hostname.includes('guardian') && row.username === 'root')
			) && (
				<Tooltip title="Restart Process" arrow placement="right">
					<IconButton
						type="button"
						color="warning"
						size="small"
						disabled={row.status != 'online'}
						onClick={(e) => {
							restartProcess(row);
							e.stopPropagation();
						}}
						sx={{
							padding: 0
						}}
					>
						<RestartAlt />
					</IconButton>
				</Tooltip>
			)}
			{Boolean(row.status != 'online') && (
				<Tooltip title="Start Process" arrow placement="right">
					<IconButton
						type="button"
						color="success"
						size="small"
						disabled={row.status === 'online'}
						onClick={(e) => {
							startProcess(row);
							e.stopPropagation();
						}}
						sx={{
							padding: 0
						}}
					>
						<PlayArrow />
					</IconButton>
				</Tooltip>
			)}
		</Box>
	);

	return (
		<DataGrid
			data={data?.data}
			columns={columns}
			router={Pm2Router}
			inlineActions={inlineActions}
			onClickRefetch={refetch}
			positionActionsColumn="first"
			pageName={'rtAdmin-pm2'}
			loading={isLoading}
			totalRows={data?.totalCount}
		/>
	);
};
